// MARK://dhk8/admin-shell/lib/clients/print-stations-rest
// Escrituras + lecturas ricas de Estaciones de impresión (dhk8-pos-printing).
//
// El MODELO real (verificado en services/pos-printing): una `PosPrintStation`
// (id, tenantId, branchId?, name, sortOrder + campos de cloud-pairing
// env/pairingCode/pollSecretHash/lastSeenAt) tiene N `PosPrintStationOutput`
// (id, stationId, type KDS|PRINTER, name, paperWidth 58mm|80mm|A4, templateKind
// KITCHEN/RECEIPT/SLIP-thermal, enabled). Las impresoras físicas las posee el
// Bridge; el backend solo conoce estaciones lógicas + salidas + el pairing cloud.
//
// SEPARACIÓN DE LECTURAS (decisión, sin codegen):
//   • `print-stations.ts` → `listStations()` (GraphQL `printStations`) sigue
//     siendo la fuente del dropdown Catálogo→Categorías (id/name/sortOrder). NO
//     se toca.
//   • Esta página (Estaciones de impresión) necesita además las SALIDAS y el
//     estado ONLINE/pairing, que el grafo `PrintStation` no expone completo
//     (no hay lastSeenAt/pairingCode). Por eso aquí leemos por REST:
//       - GET /v1/printing/stations          → StationDto[] (con outputs)
//       - GET /v1/print/relay/stations       → RelayStationDto[] (online/lastSeenAt/env)
//     y unimos por stationId. Evita regenerar gql/ (codegen) — el contrato REST
//     ya da ambos shapes.
//
// Rutas (verificadas en services/pos-printing/src/contract.ts → PRINTING_ROUTES).
// Auth: vía apiFetch/apiData (JWT Bearer + x-dhk8-tenant-id; mismo patrón POS).
// tenantId/env/branchId del relay se derivan del JWT — NO van en el body.

import { svcUrl, apiData } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'

const SVC = 'dhk8-pos-printing'

export type OutputType = 'KDS' | 'PRINTER'
export type PaperWidth = '58mm' | '80mm' | 'A4'
export type TemplateKind = 'KITCHEN-thermal' | 'RECEIPT-thermal' | 'SLIP-thermal'

export interface StationOutput {
  id: string
  stationId: string
  type: OutputType | string
  name: string
  paperWidth: PaperWidth | null
  templateKind: TemplateKind | null
  enabled: boolean
}

export interface Station {
  id: string
  tenantId: string
  branchId: string | null
  name: string
  sortOrder: number
  outputs: StationOutput[]
  // Relay (cloud-pairing). null cuando la estación no tiene registro de relay.
  online: boolean
  lastSeenAt: string | null
  env: string | null
}

export interface StationCreateInput {
  name: string
  sortOrder?: number
  branchId?: string | null
}
export interface StationUpdateInput {
  name?: string
  sortOrder?: number
  branchId?: string | null
}

export interface OutputCreateInput {
  type: OutputType
  name: string
  paperWidth?: PaperWidth | null
  templateKind?: TemplateKind | null
  enabled?: boolean
}
export interface OutputUpdateInput {
  type?: OutputType
  name?: string
  paperWidth?: PaperWidth | null
  templateKind?: TemplateKind | null
  enabled?: boolean
}

// ── Shapes que devuelve el backend (DTOs del contrato) ───────────────────────
interface ApiOutput {
  id: string; stationId: string; type: string; name: string
  paperWidth: string | null; templateKind: string | null; enabled: boolean
}
interface ApiStation {
  id: string; tenantId: string; branchId: string | null; name: string
  sortOrder: number; outputs: ApiOutput[]
}
interface ApiRelayStation {
  stationId: string; name: string; env: string | null; branchId: string | null
  online: boolean; lastSeenAt: string | null
}

function toOutput(o: ApiOutput): StationOutput {
  return {
    id: o.id, stationId: o.stationId, type: o.type, name: o.name,
    paperWidth: (o.paperWidth ?? null) as PaperWidth | null,
    templateKind: (o.templateKind ?? null) as TemplateKind | null,
    enabled: o.enabled,
  }
}

// ── PREVIEW seed (VITE_API_STUB=1) ───────────────────────────────────────────
const now = Date.now()
const previewStations: Station[] = [
  {
    id: 'stn-cocina', tenantId: 'preview', branchId: null, name: 'Cocina', sortOrder: 1,
    online: true, lastSeenAt: new Date(now - 5_000).toISOString(), env: 'live',
    outputs: [
      { id: 'out-kds-cocina', stationId: 'stn-cocina', type: 'KDS', name: 'Pantalla cocina', paperWidth: null, templateKind: null, enabled: true },
      { id: 'out-pr-cocina', stationId: 'stn-cocina', type: 'PRINTER', name: 'Comandera cocina', paperWidth: '80mm', templateKind: 'KITCHEN-thermal', enabled: true },
    ],
  },
  {
    id: 'stn-barra', tenantId: 'preview', branchId: null, name: 'Barra', sortOrder: 2,
    online: false, lastSeenAt: new Date(now - 120_000).toISOString(), env: 'live',
    outputs: [
      { id: 'out-pr-barra', stationId: 'stn-barra', type: 'PRINTER', name: 'Ticket barra', paperWidth: '58mm', templateKind: 'SLIP-thermal', enabled: true },
    ],
  },
  {
    id: 'stn-caja', tenantId: 'preview', branchId: null, name: 'Caja', sortOrder: 3,
    online: false, lastSeenAt: null, env: null, // sin emparejar
    outputs: [],
  },
]
const mid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`

// ── Lecturas ──────────────────────────────────────────────────────────────────

/** Lista estaciones CON salidas + estado relay (online/lastSeenAt/env). Une las
 *  dos lecturas REST por stationId. La página la usa para la tabla + el badge. */
export async function listStationsFull(signal?: AbortSignal): Promise<Station[]> {
  if (PREVIEW_STUB) return mock(previewStations)
  const init = signal ? { signal } : undefined
  // Relay puede no estar disponible (404/empty) — degradar a "sin emparejar".
  const [stationsRes, relayRes] = await Promise.all([
    apiData<{ items: ApiStation[] }>(svcUrl(SVC, '/v1/printing/stations'), init),
    apiData<{ items: ApiRelayStation[] }>(svcUrl(SVC, '/v1/print/relay/stations'), init).catch(() => ({ items: [] as ApiRelayStation[] })),
  ])
  const relayBy = new Map<string, ApiRelayStation>()
  for (const r of relayRes.items ?? []) relayBy.set(r.stationId, r)
  return (stationsRes.items ?? []).map((s): Station => {
    const relay = relayBy.get(s.id)
    return {
      id: s.id, tenantId: s.tenantId, branchId: s.branchId ?? null, name: s.name,
      sortOrder: s.sortOrder, outputs: (s.outputs ?? []).map(toOutput),
      online: relay?.online ?? false,
      lastSeenAt: relay?.lastSeenAt ?? null,
      env: relay?.env ?? null,
    }
  }).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
}

// ── Escrituras: estación ────────────────────────────────────────────────────

export async function createStation(input: StationCreateInput): Promise<ApiStation> {
  if (PREVIEW_STUB) {
    const st: Station = { id: mid('stn'), tenantId: 'preview', branchId: input.branchId ?? null, name: input.name, sortOrder: input.sortOrder ?? 0, outputs: [], online: false, lastSeenAt: null, env: null }
    previewStations.push(st)
    return mock({ id: st.id, tenantId: st.tenantId, branchId: st.branchId, name: st.name, sortOrder: st.sortOrder, outputs: [] })
  }
  const body: Record<string, unknown> = { name: input.name }
  if (input.sortOrder != null) body['sortOrder'] = input.sortOrder
  if (input.branchId) body['branchId'] = input.branchId
  return apiData<ApiStation>(svcUrl(SVC, '/v1/printing/stations'), { method: 'POST', body })
}

export async function updateStation(id: string, input: StationUpdateInput): Promise<ApiStation> {
  if (PREVIEW_STUB) {
    const st = previewStations.find((x) => x.id === id)
    if (st) {
      if (input.name !== undefined) st.name = input.name
      if (input.sortOrder !== undefined) st.sortOrder = input.sortOrder
      if (input.branchId !== undefined) st.branchId = input.branchId ?? null
    }
    return mock({ id, tenantId: 'preview', branchId: st?.branchId ?? null, name: st?.name ?? '', sortOrder: st?.sortOrder ?? 0, outputs: [] })
  }
  const body: Record<string, unknown> = {}
  if (input.name !== undefined) body['name'] = input.name
  if (input.sortOrder !== undefined) body['sortOrder'] = input.sortOrder
  if (input.branchId !== undefined) body['branchId'] = input.branchId // null limpia la sucursal
  return apiData<ApiStation>(svcUrl(SVC, `/v1/printing/stations/${id}`), { method: 'PATCH', body })
}

export async function deleteStation(id: string): Promise<void> {
  if (PREVIEW_STUB) {
    const i = previewStations.findIndex((x) => x.id === id)
    if (i >= 0) previewStations.splice(i, 1)
    return mock(undefined)
  }
  await apiData<{ id: string }>(svcUrl(SVC, `/v1/printing/stations/${id}`), { method: 'DELETE' })
}

// ── Escrituras: salidas (outputs) ────────────────────────────────────────────

export async function createOutput(stationId: string, input: OutputCreateInput): Promise<ApiOutput> {
  if (PREVIEW_STUB) {
    const st = previewStations.find((x) => x.id === stationId)
    const out: StationOutput = {
      id: mid('out'), stationId, type: input.type, name: input.name,
      paperWidth: input.type === 'PRINTER' ? (input.paperWidth ?? null) : null,
      templateKind: input.type === 'PRINTER' ? (input.templateKind ?? null) : null,
      enabled: input.enabled ?? true,
    }
    st?.outputs.push(out)
    return mock(out as ApiOutput)
  }
  const body: Record<string, unknown> = { type: input.type, name: input.name }
  if (input.type === 'PRINTER') {
    if (input.paperWidth) body['paperWidth'] = input.paperWidth
    if (input.templateKind) body['templateKind'] = input.templateKind
  }
  if (input.enabled !== undefined) body['enabled'] = input.enabled
  return apiData<ApiOutput>(svcUrl(SVC, `/v1/printing/stations/${stationId}/outputs`), { method: 'POST', body })
}

export async function updateOutput(outputId: string, input: OutputUpdateInput): Promise<ApiOutput> {
  if (PREVIEW_STUB) {
    let found: StationOutput | undefined
    for (const st of previewStations) { const o = st.outputs.find((x) => x.id === outputId); if (o) { found = o; break } }
    if (found) {
      if (input.type !== undefined) found.type = input.type
      if (input.name !== undefined) found.name = input.name
      if (input.enabled !== undefined) found.enabled = input.enabled
      // Coherencia KDS: sin paperWidth/templateKind.
      const isPrinter = (input.type ?? found.type) === 'PRINTER'
      found.paperWidth = isPrinter ? ((input.paperWidth ?? found.paperWidth) ?? null) : null
      found.templateKind = isPrinter ? ((input.templateKind ?? found.templateKind) ?? null) : null
    }
    return mock((found ?? { id: outputId, stationId: '', type: 'KDS', name: '', paperWidth: null, templateKind: null, enabled: true }) as ApiOutput)
  }
  const body: Record<string, unknown> = {}
  if (input.type !== undefined) body['type'] = input.type
  if (input.name !== undefined) body['name'] = input.name
  if (input.enabled !== undefined) body['enabled'] = input.enabled
  // paperWidth/templateKind: si pasa a KDS, mandamos null para limpiarlos.
  if (input.type === 'KDS') {
    body['paperWidth'] = null
    body['templateKind'] = null
  } else {
    if (input.paperWidth !== undefined) body['paperWidth'] = input.paperWidth
    if (input.templateKind !== undefined) body['templateKind'] = input.templateKind
  }
  return apiData<ApiOutput>(svcUrl(SVC, `/v1/printing/outputs/${outputId}`), { method: 'PATCH', body })
}

export async function deleteOutput(outputId: string): Promise<void> {
  if (PREVIEW_STUB) {
    for (const st of previewStations) {
      const i = st.outputs.findIndex((x) => x.id === outputId)
      if (i >= 0) { st.outputs.splice(i, 1); break }
    }
    return mock(undefined)
  }
  await apiData<{ id: string }>(svcUrl(SVC, `/v1/printing/outputs/${outputId}`), { method: 'DELETE' })
}

// ── Relay: resolver código de emparejamiento ─────────────────────────────────

export interface ResolvedStation { stationId: string; name: string }

/** POST /v1/print/relay/stations/resolve ({pairingCode}) → {stationId,name}.
 *  El dispositivo adicional teclea el código del Bridge; el backend acota por
 *  tenant+env (del JWT). */
export async function resolvePairingCode(pairingCode: string): Promise<ResolvedStation> {
  if (PREVIEW_STUB) {
    const code = pairingCode.trim().toUpperCase()
    // Demo: "COCINA"/"BARRA" o cualquier código → primera estación con relay.
    const st = previewStations.find((s) => s.name.toUpperCase() === code) ?? previewStations.find((s) => s.lastSeenAt != null)
    if (!st) throw new Error('Código no encontrado')
    return mock({ stationId: st.id, name: st.name })
  }
  return apiData<ResolvedStation>(svcUrl(SVC, '/v1/print/relay/stations/resolve'), {
    method: 'POST',
    body: { pairingCode: pairingCode.trim() },
  })
}
